import type {
  Entity,
  IOrganizationData,
  IUser,
  IntegrationEnvironment,
  IntegrationVendor,
  Play,
  PlaySource,
  Playbook,
  Resolution,
  ShareLink,
  SharingPolicy,
  TimePeriod,
  Workspace,
  WorkspaceFolder,
  WorkspaceMember,
} from '@carboncopyco/types';
import { type Analytics, AnalyticsBrowser } from '@segment/analytics-next';
import type {
  PageParams,
  UserParams,
} from '@segment/analytics-next/dist/pkg/core/arguments-resolver';
import constate from 'constate';
import { useCallback, useState } from 'react';
import { transformToSnakeCase } from '../../helpers';
import { useAsyncEffect } from '../../hooks';
import { useHubspot } from '../hooks';

export interface TrackEventProperties {
  entityType?: string;
  tourId?: string | number;
  demoId?: string | number;
  stepId?: string | number;
  cloneId?: string | number;
  recordingId?: string | number;
  presentationId?: string | number;
  location?: string;
  shareData?: Partial<ShareLink>;
  demoName?: string;
  forClient?: string;
  variables?: Record<string, any>;
  entity?: Entity;
  checkedItem?: any;
  policy?: SharingPolicy;
  frameId?: string | number;
  timestamp?: number;
  sharingApproved?: boolean;
  demoRevision?: number;
  response?: { status: number; url: string };
  error?: string;
  workspace?: Workspace;
  workspaceMember?: WorkspaceMember;
  workspaceId?: Workspace['id'];
  workspaceName?: Workspace['workspaceName'];
  workspaceType?: Workspace['workspaceType'];
  period?: string;
  resolution?: Resolution;
  insertIndex?: number;
  numberOfAddedFrames?: number;
  demoType?: 'overlay' | 'clone';
  tabName?: string;
  framesOrder?: number[];
  saveType?: string;
  organizationId?: string;
  integrationId?: string;
  integrationEnvironment?: IntegrationEnvironment;
  integrationType?: IntegrationVendor;
  dashboardTab?: 'demo' | 'tour';
  dashboardRangeFilter?: TimePeriod;
  dashboardDownloadCsv?: 'presenters' | 'visitors';
  dashboardShortSessions?: 'include' | 'exclude';
  dashboardInternalSessions?: 'include' | 'exclude';
  dashboardWorkspaceFilter?: Workspace['id'][];
  dashboardSection?: string;
  dashboardTableTab?: 'presentations' | 'visits';
  functionName?: string;
  userRole?: IUser['role'];
  email?: IUser['email'];
  firstName?: IUser['firstName'];
  lastName?: IUser['lastName'];
  organization?: IOrganizationData;
  folderId?: WorkspaceFolder['id'];
  playbookId?: Playbook['id'];
  playId?: Play['id'];
  app?: string;
  externalUrlId?: string;
  playSource?: PlaySource;
}

interface AnalyticsProviderProps {
  segmentKey: string;
}

type IdsProperties = Pick<
  TrackEventProperties,
  | 'tourId'
  | 'demoId'
  | 'presentationId'
  | 'recordingId'
  | 'stepId'
  | 'cloneId'
  | 'workspaceId'
  | 'folderId'
  | 'playbookId'
  | 'externalUrlId'
>;

type AnalyticsUserData = Pick<
  IUser,
  'firstName' | 'lastName' | 'name' | 'email' | 'id'
>;

const useAnalyticsContext = ({ segmentKey }: AnalyticsProviderProps) => {
  const [analytics, setAnalytics] = useState<Analytics | undefined>();
  const [userData, setUserData] = useState<AnalyticsUserData>();
  const [organization, setOrganization] = useState<IOrganizationData>();
  const [applicationSource, setApplicationSource] = useState<string>();
  const [applicationVersion, setApplicationVersion] = useState<string>();
  const [isAnalyticsReady, setIsAnalyticsReady] = useState<boolean>(false);
  const hubspot = useHubspot();

  useAsyncEffect(async () => {
    try {
      const [response] = await AnalyticsBrowser.load({
        writeKey: segmentKey,
      });
      setAnalytics(response);
    } catch (_error) {
      console.error('Error while trying to fetch segment analytics');
      setAnalytics(undefined);
    }
  }, [segmentKey]);

  const trackEvent = useCallback(
    (eventName: string, properties: TrackEventProperties) => {
      if (
        !userData ||
        !organization ||
        !applicationSource ||
        !applicationVersion ||
        !hubspot ||
        !analytics
      ) {
        return;
      }
      const eventProps = { ...properties };
      if (eventProps.shareData) {
        const { token, shareUrl, ...shareData } = eventProps.shareData;
        eventProps.shareData = shareData;
      }

      const ids: Array<keyof IdsProperties> = [
        'tourId',
        'demoId',
        'presentationId',
        'recordingId',
        'stepId',
        'cloneId',
        'workspaceId',
        'folderId',
        'playbookId',
      ];

      ids.forEach((key) => {
        if (eventProps[key]) {
          eventProps[key] = String(eventProps[key]);
        }
      });

      const newArgs = {
        properties: eventProps,
        user: userData,
        organization,
        applicationSource,
        applicationVersion,
      };
      hubspot?.setTrackEvent({
        eventId: eventName,
        value: JSON.stringify(transformToSnakeCase(newArgs)),
      });
      return analytics?.track(eventName, transformToSnakeCase(newArgs));
    },
    [
      userData,
      organization,
      applicationSource,
      applicationVersion,
      hubspot,
      analytics,
    ]
  );

  const identify = useCallback(
    (...args: UserParams) => {
      hubspot?.setIdentity(args.email, ...transformToSnakeCase(args));
      analytics?.identify(...transformToSnakeCase(args));
    },
    [analytics, hubspot]
  );

  const page = useCallback(
    (...args: PageParams) => {
      hubspot?.setPathPageView(location.pathname);
      hubspot?.setTrackPageView();
      analytics?.page(...transformToSnakeCase(args));
    },
    [analytics, hubspot]
  );

  const init = useCallback(
    ({
      userData,
      organization,
      source,
      version,
      withHubspot,
    }: {
      userData: AnalyticsUserData;
      organization: IOrganizationData;
      source: string;
      version: string;
      withHubspot?: boolean;
    }) => {
      setUserData(userData);
      setOrganization(organization);
      setApplicationSource(source);
      setApplicationVersion(version);
      setIsAnalyticsReady(true);
      hubspot && withHubspot && hubspot.init();
    },
    [hubspot]
  );
  return {
    trackEvent,
    identify,
    page,
    init,
    setUserData,
    setOrganization,
    setApplicationSource,
    setApplicationVersion,
    isAnalyticsReady,
    analytics,
    userData: analytics?.user(),
  };
};

export const [AnalyticsProvider, useAnalytics] = constate(useAnalyticsContext);
